import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApplicationPackage } from 'src/app/enums/application-package.enum';
import { Service } from 'src/app/enums/service.enum';
import { CustomHttpUrlEncodingCodec } from 'src/app/main/encoder';
import { Child } from 'src/app/model/child.model';
import { CensorshipPreviewHistory } from 'src/app/model/cms-vod-core/content-controller/censhorship-models/censorship-preview-history.model';
import { ContentStatusCount } from 'src/app/model/cms-vod-core/content-controller/content-count-controller.model';
import { ContentTypeStatusCount } from 'src/app/model/cms-vod-core/content-controller/content-type-status-count.model';
import {
  ContentStatusGroupCount,
  ContentTypeStatusServiceCount,
} from 'src/app/model/cms-vod-core/content-controller/content-type-status-service-count.model';
import { PageOfContentWithoutPosterResponse } from 'src/app/model/cms-vod-core/content-controller/content-without-posters.model';
import { Language } from 'src/app/model/cms-vod-core/content-controller/language.model';
import { LicenseInfo } from 'src/app/model/cms-vod-core/content-controller/licence-info.model';
import { ListFolder } from 'src/app/model/cms-vod-core/content-controller/list-folder.model';
import { MediaFileResyncResult } from 'src/app/model/cms-vod-core/content-controller/media-file-resync-result.model';
import { PageOfContentMinimized } from 'src/app/model/cms-vod-core/content-controller/page-of-content-minimized.model';
import { PageOfNotApprovedContentResponse } from 'src/app/model/cms-vod-core/content-controller/page-of-not-approved-content-response.model';
import { PublishedByPlatformCount } from 'src/app/model/cms-vod-core/content-controller/published-platform-count.model';
import { ServiceTypePlatformCount } from 'src/app/model/cms-vod-core/content-controller/servicetype-platform-count.model';
import { DownloadRequestLog } from 'src/app/model/cms-vod-core/content-controller/status-log.model';
import { TypeCount } from 'src/app/model/cms-vod-core/content-controller/type-count-model';
import { VideoOperationProcessNameCount } from 'src/app/model/cms-vod-core/content-controller/videooperation-process-name-count.model';
import { MediaFileHistory } from 'src/app/model/cms-vod-core/media-file-history.model';
import { ProviderContentServiceType } from 'src/app/model/cms-vod-core/provider-content-service-type.model';
import { StatusLog } from 'src/app/model/cms-vod-core/status-log.model';
import { TotalCompletedPromoCountByUserAndPromoTypeResponse } from 'src/app/model/cms-vod-core/total-completed-promo-count-by-user-and-promo-type-response.model';
import {
  TotalFinishedTaskCountByUserResponse,
  TrailerOperationProcessesCompletedCount,
  VideoOperationUserTaskCountResponse,
} from 'src/app/model/cms-vod-core/total-finished-task-count-by-user-response.model';
import { TrailerOperationRequest } from 'src/app/model/cms-vod-core/trailer-operation.model';
import { PageableHistoryResponse } from 'src/app/model/cms-vod-core/validation-event-models/validation-history.model';
import { ComingSoonContent } from 'src/app/model/coming-soon-content.model';
import { ContentCast } from 'src/app/model/content-cast.model';
import { ContentMetadataBulkPatchRequest } from 'src/app/model/content-metadata-bulk-patch-request.model';
import {
  ContentBulkDeleteRequest,
  ContentBulkDeleteResponse,
} from 'src/app/model/content-bulk-delete.model';
import {
  ContentBulkActionRequest,
  ContentBulkActionResponse,
} from 'src/app/model/content-bulk-action.model';
import { ContentMediaFilesRequest } from 'src/app/model/content-media-file-request.model';
import { ContentMediaFile } from 'src/app/model/content-media-file.model';
import { LanguageOptionsRequest } from 'src/app/model/language-options-request.model';
import { ContentNote } from 'src/app/model/content-note.model';
import { ContentPoster } from 'src/app/model/content-poster.model';
import { Content } from 'src/app/model/content.model';
import { License } from 'src/app/model/license.model';
import { PageOfContent } from 'src/app/model/page-of-content.model';
import { ProcessRequest } from 'src/app/model/vod-operations/video-operation-process-request.model';
import { VodQueryParams } from 'src/app/model/vod-query-params.model';
import { environment } from 'src/environments/environment';
import { PageOfContentLicenseActiveButNoPlan } from '../../../model/cms-vod-core/content-controller/content-without-plan.model';
import { QueryParams } from '../../../model/query-models/query-models';
import { ValidationEventsPlatformComment } from 'src/app/model/cms-vod-core/validation-event-models/validation-events-platform-comment.model';
import moment from 'moment';
import { PageOfContentNote } from 'src/app/model/page-of-content-notes';
import { shareReplay, take } from 'rxjs/operators';
import { BannerPlatforms } from 'src/app/enums/banner-platform.enum';
import { NewBanner } from 'src/app/model/cms-banner-core/banner-models/banner-model';
import { ParentChangePrecheck } from 'src/app/model/cms-vod-core/content-controller/parent-change-precheck.model';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private readonly contentsApiUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.CONTENT;
  private readonly castApiUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.CAST;
  private readonly languageApiUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.LANGUAGE;
  private readonly bannerApiUrl = environment.endPoint + ApplicationPackage.CMS_VOD + Service.BANNER;

  constructor(private http: HttpClient) { }
  private contentCache: { [id: number]: Observable<Content> } = {};
  private checkApprovedStatusCache: { [contentId: number]: Observable<string> } = {};
  private childsCache: { [key: string]: Observable<Child[]> } = {};

  getAllContents(vodQueryParams?: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (vodQueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodQueryParams.itemCount);
    }
    if (vodQueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
    }
    if (vodQueryParams.contentType) {
      queryParams = queryParams.set('contentType', <any>vodQueryParams.contentType);
    }
    if (vodQueryParams.contentTypes) {
      queryParams = queryParams.set('contentType', <any>vodQueryParams.contentTypes);
    }
    if (vodQueryParams.status && vodQueryParams.status.length > 0) {
      queryParams = queryParams.set('status', <any>vodQueryParams.status);
    }
    if (vodQueryParams.type && vodQueryParams.type.length > 0) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.videoOperationProcessName) {
      queryParams = queryParams.set('videoOperationProcessName', <any>vodQueryParams.videoOperationProcessName);
    }
    if (vodQueryParams.year) {
      queryParams = queryParams.set('year', <any>vodQueryParams.year);
    }
    if (vodQueryParams.startTime && vodQueryParams.startTime.length > 0) {
      queryParams = queryParams.set('licenseStartTime', <any>vodQueryParams.startTime);
    }
    if (vodQueryParams.endTime && vodQueryParams.endTime.length > 0) {
      queryParams = queryParams.set('licenseEndTime', <any>vodQueryParams.endTime);
    }

    if (vodQueryParams.vfoStartTime && vodQueryParams.vfoStartTime.length > 0) {
      queryParams = queryParams.set('vfoLicenseStartTime', <any>vodQueryParams.vfoStartTime);
    }
    if (vodQueryParams.vfoEndTime && vodQueryParams.vfoEndTime.length > 0) {
      queryParams = queryParams.set('vfoLicenseEndTime', <any>vodQueryParams.vfoEndTime);
    }

    if (vodQueryParams.planStartTime) {
      queryParams = queryParams.set('planStartTime', <any>vodQueryParams.planStartTime);
    }
    if (vodQueryParams.planEndTime) {
      queryParams = queryParams.set('planEndTime', <any>vodQueryParams.planEndTime);
    }
    if (vodQueryParams.publishTime) {
      queryParams = queryParams.set('publishTime', <any>vodQueryParams.publishTime);
    }
    if (vodQueryParams.unpublishedTime) {
      queryParams = queryParams.set('unpublishedTime', <any>vodQueryParams.unpublishedTime);
    }

    if (vodQueryParams.broadCastTime && vodQueryParams.broadCastTime.length > 0) {
      queryParams = queryParams.set('broadCastTime', <any>vodQueryParams.broadCastTime);
    }

    if (vodQueryParams.actors) {
      queryParams = queryParams.set('actor', <any>vodQueryParams.actors);
    }
    if (vodQueryParams.directors) {
      queryParams = queryParams.set('director', <any>vodQueryParams.directors);
    }
    if (vodQueryParams.duration) {
      queryParams = queryParams.set('duration', <any>vodQueryParams.duration);
    }
    if (vodQueryParams.minDuration != undefined && vodQueryParams.minDuration != null) {
      queryParams = queryParams.set('minDuration', <any>vodQueryParams.minDuration);
    }
    if (vodQueryParams.maxDuration != undefined && vodQueryParams.maxDuration != null) {
      queryParams = queryParams.set('maxDuration', <any>vodQueryParams.maxDuration);
    }
    if (vodQueryParams.vodType) {
      queryParams = queryParams.set('vodType', <any>vodQueryParams.vodType);
    }
    if (vodQueryParams.platform) {
      queryParams = queryParams.set('platform', <any>vodQueryParams.platform);
    }
    if (vodQueryParams.licenseServiceType) {
      queryParams = queryParams.set('licenseServiceType', <any>vodQueryParams.licenseServiceType);
    }
    if (vodQueryParams.serviceType) {
      queryParams = queryParams.set('serviceType', <any>vodQueryParams.serviceType);
    }
    if (vodQueryParams.title) {
      queryParams = queryParams.set('title', <any>vodQueryParams.title);
    }
    if (vodQueryParams.videoFormat) {
      queryParams = queryParams.set('videoFormat', <any>vodQueryParams.videoFormat);
    }
    if (vodQueryParams.aspectRatio) {
      queryParams = queryParams.set('aspectRatio', <any>vodQueryParams.aspectRatio);
    }
    if (vodQueryParams.imdbRating) {
      queryParams = queryParams.set('imdbRating', <any>vodQueryParams.imdbRating);
    }
    if (vodQueryParams.provider) {
      queryParams = queryParams.set('provider', <any>vodQueryParams.provider);
    }
    if (vodQueryParams.quickFilter) {
      queryParams = queryParams.set('quickFilter', <any>vodQueryParams.quickFilter);
    }
    if (vodQueryParams.licenseTba) {
      queryParams = queryParams.set('licenseTba', <any>vodQueryParams.licenseTba);
    }
    if (vodQueryParams.guid) {
      queryParams = queryParams.set('guid', <any>vodQueryParams.guid);
    }
    if (vodQueryParams.videoOperationFailed != undefined) {
      queryParams = queryParams.set('videoOperationFailed', <any>vodQueryParams.videoOperationFailed);
    }
    if (vodQueryParams.videoOperationProcessStatus) {
      queryParams = queryParams.set('videoOperationProcessStatus', <any>vodQueryParams.videoOperationProcessStatus);
    }
    if (vodQueryParams.migrated != undefined) {
      queryParams = queryParams.set('migrated', <any>vodQueryParams.migrated);
    }
    if (vodQueryParams.labelId.length) {
      queryParams = queryParams.set('labelId', <any>vodQueryParams.labelId);
    }
    if (vodQueryParams.contentGroupIds.length) {
      queryParams = queryParams.set('contentGroupId', <any>vodQueryParams.contentGroupIds);
    }
    if (vodQueryParams.blackPeriods != undefined) {
      queryParams = queryParams.set('licenseBlackPeriod', <any>vodQueryParams.blackPeriods);
    }
    if (vodQueryParams.mwInternalCode) {
      queryParams = queryParams.set('mwInternalCode', <any>vodQueryParams.mwInternalCode);
    }
    if (vodQueryParams.videoOperationNotAssigned != undefined) {
      queryParams = queryParams.set('videoOperationNotAssigned', <any>vodQueryParams.videoOperationNotAssigned);
    }
    if (vodQueryParams.comingSoon) {
      queryParams = queryParams.set('comingSoon', <any>vodQueryParams.comingSoon);
    }
    if (vodQueryParams.genreIds.length) {
      queryParams = queryParams.set('genreId', <any>vodQueryParams.genreIds);
    }
    if (vodQueryParams.trailerWaiting != undefined) {
      queryParams = queryParams.set('trailerWaiting', <any>vodQueryParams.trailerWaiting);
    }

    if (vodQueryParams.trailerOperationNotAssigned != undefined) {
      queryParams = queryParams.set('trailerOperationNotAssigned', <any>vodQueryParams.trailerOperationNotAssigned);
    }
    if (vodQueryParams.country) {
      queryParams = queryParams.set('country', <any>vodQueryParams.country);
    }
    if (vodQueryParams.trailerPriority && vodQueryParams.trailerPriority.length > 0) {
      queryParams = queryParams.set('trailerPriority', <any>vodQueryParams.trailerPriority);
    }
    if (vodQueryParams.trailerPromo && vodQueryParams.trailerPromo.length > 0) {
      queryParams = queryParams.set('trailerPromo', <any>vodQueryParams.trailerPromo);
    }
    if (vodQueryParams.originalLanguage) {
      queryParams = queryParams.set('originalLanguage', <any>vodQueryParams.originalLanguage);
    }
    if (vodQueryParams.trailerOperationProcessNames) {
      queryParams = queryParams.set('trailerOperationProcessNames', <any>vodQueryParams.trailerOperationProcessNames);
    }
    // if (vodQueryParams.trailerNotCompleted) {
    //   queryParams = queryParams.set('trailerNotCompleted', <any>vodQueryParams.trailerNotCompleted);
    // }
    if (vodQueryParams.trailerCompleted != undefined) {
      queryParams = queryParams.set('trailerCompleted', <any>vodQueryParams.trailerCompleted);
    }
    if (vodQueryParams.castId) {
      queryParams = queryParams.set('castId', <any>vodQueryParams.castId);
    }
    if (vodQueryParams.pageable == false) {
      queryParams = queryParams.set('pageable', <any>vodQueryParams.pageable);
    }
    if (vodQueryParams.metadataSummary) {
      queryParams = queryParams.set('metadataSummary', <any>vodQueryParams.metadataSummary);
    }
    if (vodQueryParams.sortParam && vodQueryParams.sortParam.length) {
      queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
    }
    if (vodQueryParams.withoutMetadata != undefined) {
      queryParams = queryParams.set('withoutMetadata', <any>vodQueryParams.withoutMetadata);
    }
    if (vodQueryParams.withoutPlan != undefined) {
      queryParams = queryParams.set('withoutPlan', <any>vodQueryParams.withoutPlan);
    }
    if (vodQueryParams.trailerAssignee) {
      queryParams = queryParams.set('trailerAssignee', <any>vodQueryParams.trailerAssignee);
    }
    if (vodQueryParams.trailerCompletionTime) {
      queryParams = queryParams.set('trailerCompletionTime', <any>vodQueryParams.trailerCompletionTime);
    }
    if (vodQueryParams.trailerCompleted != undefined) {
      queryParams = queryParams.set('trailerCompleted', <any>vodQueryParams.trailerCompleted);
    }
    if (vodQueryParams.planServiceType) {
      queryParams = queryParams.set('planServiceType', <any>vodQueryParams.planServiceType);
    }
    if (vodQueryParams.planStatus) {
      queryParams = queryParams.set('planStatus', <any>vodQueryParams.planStatus);
    }
    if (vodQueryParams.categoryId) {
      queryParams = queryParams.set('categoryId', <any>vodQueryParams.categoryId);
    }
    if (vodQueryParams.contentStatus) {
      queryParams = queryParams.set('contentStatus', <any>vodQueryParams.contentStatus);
    }
    if (vodQueryParams.contentStatus) {
      queryParams = queryParams.set('contentStatus', <any>vodQueryParams.contentStatus);
    }
    if (vodQueryParams.seriesName) {
      queryParams = queryParams.set('seriesName', <any>vodQueryParams.seriesName);
    }
    if (vodQueryParams.seasonNo) {
      queryParams = queryParams.set('seasonNo', <any>vodQueryParams.seasonNo);
    }
    if (vodQueryParams.episodeNo) {
      queryParams = queryParams.set('episodeNo', <any>vodQueryParams.episodeNo);
    }
    if (vodQueryParams.nameSearch) {
      queryParams = queryParams.set('nameSearch', <any>vodQueryParams.nameSearch);
    }

    return this.http.get<any>(this.contentsApiUrl, { params: queryParams, observe: 'response' });
  }

  getAllContentsWithPlanProfile(vodQueryParams?: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (vodQueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodQueryParams.itemCount);
    }
    if (vodQueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
    }

    if (vodQueryParams.status && vodQueryParams.status.length > 0) {
      queryParams = queryParams.set('status', <any>vodQueryParams.status);
    }

    if (vodQueryParams.planStartTime) {
      queryParams = queryParams.set('planStartTime', <any>vodQueryParams.planStartTime);
    }
    if (vodQueryParams.planEndTime) {
      queryParams = queryParams.set('planEndTime', <any>vodQueryParams.planEndTime);
    }
    if (vodQueryParams.vodType) {
      queryParams = queryParams.set('vodType', <any>vodQueryParams.vodType);
    }
    if (vodQueryParams.guid) {
      queryParams = queryParams.set('guid', <any>vodQueryParams.guid);
    }

    if (vodQueryParams.sortParam && vodQueryParams.sortParam.length) {
      queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
    }
    if (vodQueryParams.contentStatus) {
      queryParams = queryParams.set('contentStatus', <any>vodQueryParams.contentStatus);
    }
    if (vodQueryParams.type && vodQueryParams.type.length > 0) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.seriesName) {
      queryParams = queryParams.set('seriesName', <any>vodQueryParams.seriesName);
    }
    if (vodQueryParams.seasonNo) {
      queryParams = queryParams.set('seasonNo', <any>vodQueryParams.seasonNo);
    }
    if (vodQueryParams.episodeNo) {
      queryParams = queryParams.set('episodeNo', <any>vodQueryParams.episodeNo);
    }

    return this.http.get<any>(this.contentsApiUrl + '/getcontentswithplanprofile', {
      params: queryParams,
      observe: 'response',
    });
  }

  getAllContentsWithSearchNavigationId(): Observable<any> {
    // return this.http
    //   .get<any>(this.contentsApiUrl, {observe: 'response'})
    //   .pipe(
    //     map((response) => {
    //       const data = response.body; // the data returned from your request
    //       const keys = response.headers.keys(); // keys of the response headers
    //       const headers = keys.map((key) => response.headers.get(key)); // values of the response headers

    //       // So to get the token, you need to do
    //       const xSeearchNavigationId = response.headers.get('X-SearchNavigationId');

    //       // To store you do:
    //
    //
    //
    //

    //       return data;
    //     }),
    //   );
    return this.http.get<any>(this.contentsApiUrl, { observe: 'response' });
  }

  getAllContentsWithoutPagination(vodQueryParams?: VodQueryParams): Observable<Content[]> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (vodQueryParams.page) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
    }
    if (vodQueryParams.contentType) {
      queryParams = queryParams.set('contentType', <any>vodQueryParams.contentType);
    }
    if (vodQueryParams.status) {
      queryParams = queryParams.set('status', <any>vodQueryParams.status);
    }
    if (vodQueryParams.type) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.videoOperationProcessName) {
      queryParams = queryParams.set('videoOperationProcessName', <any>vodQueryParams.videoOperationProcessName);
    }
    if (vodQueryParams.year) {
      queryParams = queryParams.set('year', <any>vodQueryParams.year);
    }
    if (vodQueryParams.startTime) {
      queryParams = queryParams.set('licenseStartTime', <any>vodQueryParams.startTime);
    }
    if (vodQueryParams.endTime) {
      queryParams = queryParams.set('licenseEndTime', <any>vodQueryParams.endTime);
    }
    if (vodQueryParams.actors.length > 0) {
      queryParams = queryParams.set('actor', <any>vodQueryParams.actors);
    }
    if (vodQueryParams.directors.length > 0) {
      queryParams = queryParams.set('director', <any>vodQueryParams.directors);
    }
    if (vodQueryParams.duration) {
      queryParams = queryParams.set('duration', <any>vodQueryParams.duration);
    }
    if (vodQueryParams.vodType) {
      queryParams = queryParams.set('vodType', <any>vodQueryParams.vodType);
    }
    if (vodQueryParams.platform) {
      queryParams = queryParams.set('platform', <any>vodQueryParams.platform);
    }
    if (vodQueryParams.serviceType) {
      queryParams = queryParams.set('serviceType', <any>vodQueryParams.serviceType);
    }
    if (vodQueryParams.title) {
      queryParams = queryParams.set('title', <any>vodQueryParams.title);
    }
    if (vodQueryParams.videoFormat) {
      queryParams = queryParams.set('videoFormat', <any>vodQueryParams.videoFormat);
    }
    if (vodQueryParams.aspectRatio) {
      queryParams = queryParams.set('aspectRatio', <any>vodQueryParams.aspectRatio);
    }
    if (vodQueryParams.imdbRating) {
      queryParams = queryParams.set('imdbRating', <any>vodQueryParams.imdbRating);
    }
    if (vodQueryParams.provider) {
      queryParams = queryParams.set('provider', <any>vodQueryParams.provider);
    }
    if (vodQueryParams.quickFilter != undefined) {
      queryParams = queryParams.set('quickFilter', <any>vodQueryParams.quickFilter);
    }
    if (vodQueryParams.licenseTba) {
      queryParams = queryParams.set('licenseTba', <any>vodQueryParams.licenseTba);
    }
    if (vodQueryParams.guid) {
      queryParams = queryParams.set('guid', <any>vodQueryParams.guid);
    }
    if (vodQueryParams.videoOperationFailed) {
      queryParams = queryParams.set('videoOperationFailed', <any>vodQueryParams.videoOperationFailed);
    }
    if (!vodQueryParams.pageable) {
      queryParams = queryParams.set('pageable', <any>vodQueryParams.pageable);
    }
    if (vodQueryParams.videoOperationProcessStatus) {
      queryParams = queryParams.set('videoOperationProcessStatus', <any>vodQueryParams.videoOperationProcessStatus);
    }
    if (vodQueryParams.mwInternalCode) {
      queryParams = queryParams.set('mwInternalCode', <any>vodQueryParams.mwInternalCode);
    }
    if (vodQueryParams.publishTime) {
      queryParams = queryParams.set('publishTime', <any>vodQueryParams.publishTime);
    }
    if (vodQueryParams.withoutPlan != undefined) {
      queryParams = queryParams.set('withoutPlan', <any>vodQueryParams.withoutPlan);
    }
    return this.http.get<Content[]>(this.contentsApiUrl, { params: queryParams });
  }

  getContentWithParams(contentName: string, year: string, contentType: string, type: string): Observable<PageOfContent> {
    return this.http.get<PageOfContent>(
      this.contentsApiUrl + `?itemCount=100&year=${year}&contentName=${contentName}&contentType=${contentType}&type=${type}`,
    );
  }

  getContentForParentSelection(contentName: string, type: string): Observable<PageOfContent> {
    return this.http.get<PageOfContent>(this.contentsApiUrl + `?&contentName=${contentName}&type=${type}`);
  }

  getContentById(id: number, bool: boolean = false): Observable<Content> {
    if (!this.contentCache[id] || bool) {
      this.contentCache[id] = this.http.get<Content>(`${this.contentsApiUrl}/${id}`).pipe(
      );
    }
    return this.contentCache[id];
  }

  // Clear Content Cache
  clearContentCache(id: number) {
    delete this.contentCache[id];
  }

  getContentByIdPromise(contentId: number): Promise<Content> {
    return this.http.get<Content>(this.contentsApiUrl + `/${contentId}`).toPromise();
  }

  saveNewContentFolder(contentId: number, folderName: string): Promise<any> {
    // @ts-ignore
    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/editfoldername?folderName=${folderName}`, {}, { responseType: 'text' }).toPromise();
  }

  getThumbnailsByUrl(url: string): Observable<any> {
    return this.http.get<any>(url);
  }

  //Get contents by series
  getContentsBySeries(series: string): Observable<Content[]> {
    return this.http.get<Content[]>(this.contentsApiUrl + `/?type${series}`);
  }

  //Create Content
  setContent(content: Content, seasons?: number[]): Observable<Content> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (seasons != null && seasons != undefined && seasons.length > 0) {
      queryParams = queryParams.set('seasons', <any>seasons);
    }
    if (seasons != null) {
      return this.http.post<Content>(this.contentsApiUrl, content, { params: queryParams });
    } else {
      return this.http.post<Content>(this.contentsApiUrl, content);
    }
    // return;
  }

  getIngestStatus(contentId: number): Observable<string> {
    return this.http.get(`${this.contentsApiUrl}/${contentId}/ingest-status`, {responseType: 'text'});
  }

  updateContent(content: Content, updateChilds?: boolean): Observable<Content> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (updateChilds) {
      queryParams = queryParams.set('updateChilds', <any>updateChilds);
    }
    return this.http.put<Content>(this.contentsApiUrl, content, { params: queryParams });
  }

  bulkPatchMetadata(request: ContentMetadataBulkPatchRequest): Observable<number[]> {
    return this.http.put<number[]>(this.contentsApiUrl + '/bulk/metadata/fields', request);
  }

  applyBulkAction(request: ContentBulkActionRequest): Observable<ContentBulkActionResponse> {
    return this.http.put<ContentBulkActionResponse>(this.contentsApiUrl + '/bulk/actions', request);
  }

  setContentHidden(contentId: number, enable: boolean): Observable<Content> {
    const queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() })
      .set('enable', <any>enable);

    return this.http.put<Content>(`${this.contentsApiUrl}/${contentId}/hidden`, null, {
      params: queryParams,
    });
  }

  updateContentPromise(content: Content, updateChilds?: boolean) {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (updateChilds) {
      queryParams = queryParams.set('updateChilds', <any>updateChilds);
    }
    return this.http.put(this.contentsApiUrl, content, { params: queryParams }).toPromise();
  }

  setTrailerWaiting(contentId: number, trailer: boolean, trailerOperation: TrailerOperationRequest): Observable<any> {
    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/settrailerwaiting/${trailer}`, trailerOperation);
  }

  // Delete Content
  deleteContent(contentId: number, option?: string): Observable<Content> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (option) {
      queryParams = queryParams.set('withOption', <any>option);
    }

    return this.http.delete<Content>(this.contentsApiUrl + `/${contentId}`, { params: queryParams });
  }

  bulkDeleteContents(request: ContentBulkDeleteRequest): Observable<ContentBulkDeleteResponse> {
    return this.http.post<ContentBulkDeleteResponse>(this.contentsApiUrl + '/bulk/delete', request);
  }

  deleteContentPermanently(contentId: number): Observable<number> {
    return this.http.delete<number>(this.contentsApiUrl + `/${contentId}/permanently`);
  }

  // Add License to Content
  addLicenseToContent(contentId: number, licenses: License[]): Observable<Content> {
    return this.http.post<Content>(this.contentsApiUrl + `/${contentId}/licenses`, licenses, { responseType: 'text' as 'json' });
  }

  // Gets List of watch folterT
  getListWatchFolder(): Observable<ContentMediaFile[]> {
    return this.http.get<ContentMediaFile[]>(this.contentsApiUrl + '/videooperations/listwatchfolder');
  }

  getContentMediaFiles(contentId?: number, mediaTypes?: string[]): Observable<ListFolder[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (mediaTypes) {
      queryParams = queryParams.set('mediaType', <any>mediaTypes);
    }
    return this.http.get<ListFolder[]>(this.contentsApiUrl + `/${contentId}/videoperations/listfolder`, {
      params: queryParams,
    });
  }

  getContentTranscodedFiles(contentId: number): Observable<ListFolder[]> {
    return this.http.get<ListFolder[]>(this.contentsApiUrl + `/${contentId}/transcoded/listfolder`);
  }

  getAllChilds(trimmed?: boolean, contentIds?: number[]): Observable<{ [key: number]: Child[] }> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (contentIds.length) {
      queryParams = queryParams.set('contentIds', <any>contentIds);
    }
    if (trimmed != null) {
      queryParams = queryParams.set('trimmed', <any>trimmed);
    }

    return this.http.get<{ [key: number]: Child[] }>(this.contentsApiUrl + `/allchilds`, {
      params: queryParams,
    });
  }

  getSectionInfoCounts(trimmed?: boolean, contentIds?: number[]): Observable<{ [key: number]: number }> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (contentIds.length) {
      queryParams = queryParams.set('contentIds', contentIds.join(','));
    }
    if (trimmed != null) {
      queryParams = queryParams.set('trimmed', <any>trimmed);
    }

    return this.http.get<{ [key: number]: number }>(this.contentsApiUrl + `/sectioninfos/count`, {
      params: queryParams,
    });
  }

  getTranscodedFileName(contentId: number): Observable<string> {
    return this.http.get<string>(this.contentsApiUrl + `/${contentId}/transcoded/filename`, { responseType: 'text' as 'json' });
  }

  // Set Media File to Content
  setMediaFile(id: number, contentMediaFilesRequest: ContentMediaFilesRequest): Observable<ContentMediaFilesRequest> {
    return this.http.post<ContentMediaFilesRequest>(this.contentsApiUrl + `/${id}/mediafiles/add`, contentMediaFilesRequest, {
      responseType: 'text' as 'json',
    });
  }

  //Remove Media File to Content
  removeMediaFile(contentId: number, contentMediaFilesRequest: ContentMediaFilesRequest): Observable<ContentMediaFilesRequest> {
    return this.http.request<ContentMediaFilesRequest>('delete', this.contentsApiUrl + `/${contentId}/mediafiles/remove`, {
      body: contentMediaFilesRequest,
      responseType: 'text' as 'json',
    });
  }

  // Check Mandotory Posters
  mandotoryCheck(contentId: number): Observable<boolean> {
    return this.http.get<boolean>(this.contentsApiUrl + `/${contentId}/posters/mandatorycheck`);
  }

  //Gets content by LicenceStartTime
  getContentsByStartTime(startTime: number): Observable<PageOfContent> {
    let queryParameters = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (startTime !== null && startTime !== undefined) {
      queryParameters = queryParameters.set('startTime', <any>startTime);
    }
    return this.http.get<PageOfContent>(this.contentsApiUrl, { params: queryParameters });
  }

  ///contents/{contentId}/posters/{posterDefinitionId}/upload
  // 2 path variable yani content id ve posterdefiniton id
  //  qyery param boolean autoPosterCreate ve updateChilds
  //  1 tane de file
  setPosterToContent(contentId: number, posterDefinitionId: number, autoPosterCreate: boolean, updateChilds: boolean, file: File): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (autoPosterCreate) {
      queryParams = queryParams.set('autoPosterCreate', <any>autoPosterCreate);
    }
    if (updateChilds) {
      queryParams = queryParams.set('updateChilds', <any>updateChilds);
    }
    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/posters/${posterDefinitionId}/upload`, formData, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  getLicenceInfo(): Observable<LicenseInfo[]> {
    return this.http.get<LicenseInfo[]>(this.contentsApiUrl + `/licenseinfo`);
  }

  getVideoOperationFailedContents(vodqueryParams: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    vodqueryParams.page = vodqueryParams.page - 1;
    if (vodqueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodqueryParams.page);
    }
    if (vodqueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodqueryParams.itemCount);
    }
    if (vodqueryParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>vodqueryParams.sortParam);
    }
    if (vodqueryParams.provider) {
      queryParams = queryParams.set('provider', <any>vodqueryParams.provider);
    }
    if (vodqueryParams.status.length > 0) {
      queryParams = queryParams.set('status', <any>vodqueryParams.status);
    }
    if (vodqueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodqueryParams.contentName);
    }
    if (vodqueryParams.guid) {
      queryParams = queryParams.set('guid', <any>vodqueryParams.guid);
    }
    return this.http.get<any>(this.contentsApiUrl + `/videooperationfailed`, {
      observe: 'response',
      params: queryParams,
    });
  }

  getContentStatusCount(): Observable<ContentStatusCount[]> {
    return this.http.get<ContentStatusCount[]>(this.contentsApiUrl + `/status/count`);
  }

  //publish content
  publishContent(contenetId: number): Observable<string> {
    return this.http.get<string>(this.contentsApiUrl + `/${contenetId}/publish`, { responseType: 'text' as 'json' });
  }

  //unpublish content
  unpublishContent(contenetId: number): Observable<Content> {
    return this.http.get<Content>(this.contentsApiUrl + `/${contenetId}/unpublish`, { responseType: 'text' as 'json' });
  }

  getContentsTypeCount(): Observable<TypeCount> {
    return this.http.get<TypeCount>(this.contentsApiUrl + `/type/count`);
  }

  getLicenceStartedNotPublished(queryParam: VodQueryParams): Observable<PageOfContentLicenseActiveButNoPlan> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (queryParam.itemCount) {
      queryParams = queryParams.set('itemCount', <any>queryParam.itemCount);
    }
    if (queryParam.page > 0) {
      queryParams = queryParams.set('page', <any>queryParam.page);
    }
    if (queryParam.sortParam) {
      queryParams = queryParams.set('sortParam', <any>queryParam.sortParam);
    }

    return this.http.get<PageOfContent>(this.contentsApiUrl + `/licensestartednotpublished`, {
      params: queryParams,
    });
  }

  approveContent(id: number, platform: string): Observable<any> {
    return this.http.get<any>(this.contentsApiUrl + `/${id}/${platform}/approve`, { responseType: 'text' as 'json' });
  }

  getContentTypeStatusCount(): Observable<ContentTypeStatusCount[]> {
    return this.http.get<ContentTypeStatusCount[]>(this.contentsApiUrl + '/contenttypestatus/count');
  }

  getCountContentStatusAndServiceAndContentType(): Observable<ContentTypeStatusServiceCount[]> {
    return this.http.get<any[]>(this.contentsApiUrl + '/contentstatusservicetypecontenttype/count');
  }

  getContentStatusGroupCount(): Observable<ContentStatusGroupCount[]> {
    return this.http.get<ContentStatusGroupCount[]>(this.contentsApiUrl + `/contentstatusgroup/count`);
  }

  getContentWithoutCensorshipPreview(vodQueryParams: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (vodQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodQueryParams.itemCount);
    }
    vodQueryParams.page = vodQueryParams.page - 1;
    if (vodQueryParams.page > -1) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.type) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
    }
    return this.http.get<any>(this.contentsApiUrl + '/withoutcensorshippreview', {
      params: queryParams,
      observe: 'response',
    });
  }

  getVideoFilesStatusCount(): Observable<VideoOperationProcessNameCount[]> {
    return this.http.get<VideoOperationProcessNameCount[]>(this.contentsApiUrl + '/videooperationprocessname/count');
  }

  getPublishedByPlatformCount(): Observable<PublishedByPlatformCount[]> {
    return this.http.get<PublishedByPlatformCount[]>(this.contentsApiUrl + '/publishedbyplatform/count');
  }

  getServiceTypePlatformCount(): Observable<ServiceTypePlatformCount[]> {
    return this.http.get<ServiceTypePlatformCount[]>(this.contentsApiUrl + '/servicetypeplatform/count');
  }

  getContentStatusLog(id: number): Observable<StatusLog[]> {
    return this.http.get<StatusLog[]>(this.contentsApiUrl + `/${id}/contentstatuslogs`);
    //GET /contents/{id}/contentstatuslogs
  }

  getContentMediaFile(id: number): Observable<StatusLog[]> {
    return this.http.get<ContentMediaFile[]>(this.contentsApiUrl + `/${id}/mediafiles`);
  }

  getSmilFileName(id: number): Observable<string> {
    return this.http.get(this.contentsApiUrl + `/${id}/smilfilename`, { responseType: 'text' });
  }

  resyncMediaFiles(id: number): Observable<MediaFileResyncResult> {
    return this.http.post<MediaFileResyncResult>(this.contentsApiUrl + `/${id}/mediafiles/resync`, {});
  }

  getContentHistory(id: number): Observable<any> {
    let params = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (id != null && id != undefined) {
      params = params.set('contentId', <any>id);
    }
    return this.http.get<any>(this.contentsApiUrl + `/history`, { params });
    //GET /contents/history
  }

  searchCastByNAme(name: string): Observable<any> {
    return this.http.get<any>(this.castApiUrl + `?name=` + name);
  }

  getLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(this.languageApiUrl);
  }

  addSingleSeasonToSerie(content: Content, seasons?: number[]): Observable<Content> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (seasons != null && seasons != undefined && seasons.length > 0) {
      queryParams = queryParams.set('seasons', <any>seasons);
    }
    return this.http.post<Content>(this.contentsApiUrl, content, { params: queryParams });
  }

  addEpisodeToSeason(content: Content, episodeCount: number): Observable<Content> {
    return this.http.post<Content>(this.contentsApiUrl + `?episodeCount=${episodeCount}`, content);
  }

  // Get Content Notes
  getContentNotes(contentId: number, page?: number, itemCount?: number): Observable<PageOfContentNote> {
    let queryParams: HttpParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    page = page - 1;
    if (page > -1) {
      queryParams = queryParams.set('page', <any>page);
    }
    if (itemCount) {
      queryParams = queryParams.set('itemCount', <any>itemCount);
    }

    return this.http.get<PageOfContentNote>(this.contentsApiUrl + `/${contentId}/notes`, { params: queryParams });
  }

  // Add Content Notes
  addContentNote(noteObj: ContentNote, id: number): Observable<ContentNote> {
    return this.http.post<ContentNote>(this.contentsApiUrl + `/${id}/notes`, noteObj, { responseType: 'text' as 'json' });
  }

  //Delete Content Note
  deleteContentNote(id: number): Observable<string> {
    return this.http.delete<string>(this.contentsApiUrl + `/${id}/notes`, {
      responseType: 'text' as 'json',
    });
  }

  //Edit Content Note
  editContentNote(noteObj: ContentNote): Observable<ContentNote> {
    return this.http.put<ContentNote>(this.contentsApiUrl + `/notes`, noteObj, { responseType: 'text' as 'json' });
  }

  //GetTrailers
  listTrailerWatchFolder(): Observable<ContentMediaFile[]> {
    return this.http.get<ContentMediaFile[]>(this.contentsApiUrl + `/trailers/listwatchfolder`);
  }

  //Add Trailer
  moveTrailerFile(contentId: number, fileName: string): Observable<any> {
    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/trailers/${fileName}/add`, null, {
      responseType: 'text' as 'json',
    });
  }

  //Remove trailer
  removeTrailerFile(contentId: number): Observable<any> {
    return this.http.get<any>(this.contentsApiUrl + `/${contentId}/trailers/remove`, { responseType: 'text' as 'json' });
  }

  //Get Content Metadata History
  getContentMetadataHistory(contentId: number): Observable<PageableHistoryResponse> {
    return this.http.get<PageableHistoryResponse>(this.contentsApiUrl + `/${contentId}/metadata/history`);
  }

  //Get content childs
  getContentChildsById(contentId?: number, seasonNumber?: number): Observable<Child[]> {
    const cacheKey = `${contentId || 'noId'}-${seasonNumber !== null && seasonNumber !== undefined ? seasonNumber : 'noSeason'}`;

    if (this.childsCache[cacheKey]) {
      return this.childsCache[cacheKey];
    }
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (seasonNumber != null) {
      queryParams = queryParams.set('seasonNumber', seasonNumber.toString());
    }
    this.childsCache[cacheKey] = this.http.get<Child[]>(`${this.contentsApiUrl}/${contentId}/childs`, { params: queryParams });

    return this.childsCache[cacheKey];
  }

  //Get Posters of content

  getContentPosters(contentId: number): Observable<ContentPoster[]> {
    return this.http.get<ContentPoster[]>(this.contentsApiUrl + `/${contentId}/posters`);
  }

  // Removing Poster from content
  removePoster(contentId: number, posterDefinitionId: number): Observable<string> {
    return this.http.delete<string>(this.contentsApiUrl + `/${contentId}/posters/${posterDefinitionId}`, {
      responseType: 'text' as 'json',
    });
  }

  getAllNotApprovedContent(): Observable<any> {
    return this.http.get<any>(this.contentsApiUrl + `/notapproved`);
  }

  createCatchupMediaDownloadById(contentId: number): Observable<number> {
    return this.http.post<number>(this.contentsApiUrl + `/${contentId}/cutvmediafiledownloadrequest`, null, {
      responseType: 'text' as 'json',
    });
  }

  getCatchupMediaDownloadStatusLog(contentId: number): Observable<DownloadRequestLog[]> {
    return this.http.get<DownloadRequestLog[]>(this.contentsApiUrl + `/${contentId}/cutvmediadownloadrequests`);
  }

  getCensorshipPreviewHistory(contentId: number, page?: number, itemCount?: number): Observable<CensorshipPreviewHistory> {
    let queryParams: HttpParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (page > -1) {
      queryParams = queryParams.set('page', <any>page);
    }
    if (itemCount) {
      queryParams = queryParams.set('itemCount', <any>itemCount);
    }

    return this.http.get<CensorshipPreviewHistory>(this.contentsApiUrl + `/${contentId}/censorshippreview/history`);
  }

  getContentMediaFilesByMediaType(contentId: number, mediaTypes: string[]): Observable<ContentMediaFile[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (mediaTypes) {
      queryParams = queryParams.set('mediaType', <any>mediaTypes);
    }
    return this.http.get<ContentMediaFile[]>(this.contentsApiUrl + `/${contentId}/mediafiles`, { params: queryParams });
  }

  getMediaFileHistory(contentId?: number, mediaType?: string): Observable<MediaFileHistory[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (mediaType) {
      queryParams = queryParams.set('mediaType', <any>mediaType);
    }
    return this.http.get<MediaFileHistory[]>(this.contentsApiUrl + `/${contentId}/contentmediafiles/history`, {
      params: queryParams,
    });
  }

  getTrailerWaitingContents(itemCount?: number, page?: number): Observable<PageOfContent> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (itemCount) {
      queryParams = queryParams.set('itemCount', <any>itemCount);
    }

    if (page > -1) {
      queryParams = queryParams.set('page', <any>page);
    }

    return this.http.get<PageOfContent>(this.contentsApiUrl + `/trailerwaitingcontents`, { params: queryParams });
  }

  getAllContentsWithoutPoster(vodqueryParams: VodQueryParams): Observable<PageOfContentWithoutPosterResponse> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    vodqueryParams.page = vodqueryParams.page - 1;
    if (vodqueryParams.page > -1) {
      queryParams = queryParams.set('page', <any>vodqueryParams.page);
    }

    if (vodqueryParams.provider) {
      queryParams = queryParams.set('provider', <any>vodqueryParams.provider);
    }

    if (vodqueryParams.type && vodqueryParams.type.length > 0) {
      queryParams = queryParams.set('type', <any>vodqueryParams.type);
    }

    if (vodqueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodqueryParams.itemCount);
    }
    if (vodqueryParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>vodqueryParams.sortParam);
    }

    return this.http.get<PageOfContentWithoutPosterResponse>(this.contentsApiUrl + `/withoutposters`, { params: queryParams });
  }

  getAllContentsWithoutValidMetadata(metadataParams: VodQueryParams): Observable<PageOfContentMinimized> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    metadataParams.page = metadataParams.page - 1;
    if (metadataParams.page > 0) {
      queryParams = queryParams.set('page', <any>metadataParams.page);
    }
    if (metadataParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>metadataParams.itemCount);
    }
    if (metadataParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>metadataParams.sortParam);
    }
    return this.http.get<PageOfContentMinimized>(this.contentsApiUrl + `/withoutvalidmetadata`, { params: queryParams });
  }

  getContentProviderServicetypeContenttypeCount(licenseRangeTime?: number[]): Observable<ProviderContentServiceType[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (licenseRangeTime && licenseRangeTime.length != 0) {
      queryParams = queryParams.set('licenseStartTimeRange', <any>licenseRangeTime);
    }
    return this.http.get<ProviderContentServiceType[]>(this.contentsApiUrl + '/contentproviderservicetypecontenttype/count', {
      params: queryParams,
    });
  }

  getContentHaveActiveLicenseButNoPlan(vodqueryParams: VodQueryParams): Observable<PageOfContentLicenseActiveButNoPlan> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    vodqueryParams.page = vodqueryParams.page - 1;
    if (vodqueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodqueryParams.page);
    }
    if (vodqueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodqueryParams.itemCount);
    }
    if (vodqueryParams.sortParam) {
      queryParams = queryParams.set('sortParam', <any>vodqueryParams.sortParam);
    }
    return this.http.get<PageOfContentLicenseActiveButNoPlan>(this.contentsApiUrl + '/withoutplan', { params: queryParams });
  }

  getContentReport(vodQueryParams?: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });
    if (vodQueryParams) {
      if (vodQueryParams.contentName) {
        queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
      }
      if (vodQueryParams.status.length > 0) {
        queryParams = queryParams.set('status', <any>vodQueryParams.status);
      }
      if (vodQueryParams.guid) {
        queryParams = queryParams.set('guid', <any>vodQueryParams.guid);
      }
      if (vodQueryParams.sortParam) {
        queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
      }
      if (vodQueryParams.contentType) {
        queryParams = queryParams.set('contentType', <any>vodQueryParams.contentType);
      }
      if (vodQueryParams.contentTypes) {
        queryParams = queryParams.set('contentType', <any>vodQueryParams.contentTypes);
      }
      if (vodQueryParams.type) {
        queryParams = queryParams.set('type', <any>vodQueryParams.type);
      }
      if (vodQueryParams.comingSoon) {
        queryParams = queryParams.set('comingSoon', <any>vodQueryParams.comingSoon);
      }
      if (vodQueryParams.videoOperationProcessName) {
        queryParams = queryParams.set('videoOperationProcessName', <any>vodQueryParams.videoOperationProcessName);
      }
      if (vodQueryParams.year) {
        queryParams = queryParams.set('year', <any>vodQueryParams.year);
      }
      if (vodQueryParams.startTime && vodQueryParams.startTime.length > 0) {
        queryParams = queryParams.set('licenseStartTime', <any>vodQueryParams.startTime);
      }
      if (vodQueryParams.endTime && vodQueryParams.endTime.length > 0) {
        queryParams = queryParams.set('licenseEndTime', <any>vodQueryParams.endTime);
      }
      if (vodQueryParams.actors) {
        queryParams = queryParams.set('actor', <any>vodQueryParams.actors);
      }
      if (vodQueryParams.directors) {
        queryParams = queryParams.set('director', <any>vodQueryParams.directors);
      }
      if (vodQueryParams.duration) {
        queryParams = queryParams.set('duration', <any>vodQueryParams.duration);
      }
      if (vodQueryParams.vodType) {
        queryParams = queryParams.set('vodType', <any>vodQueryParams.vodType);
      }
      if (vodQueryParams.platform) {
        queryParams = queryParams.set('platform', <any>vodQueryParams.platform);
      }
      if (vodQueryParams.licenseServiceType) {
        queryParams = queryParams.set('licenseServiceType', <any>vodQueryParams.licenseServiceType);
      }
      if (vodQueryParams.serviceType) {
        queryParams = queryParams.set('serviceType', <any>vodQueryParams.serviceType);
      }
      if (vodQueryParams.title) {
        queryParams = queryParams.set('title', <any>vodQueryParams.title);
      }
      if (vodQueryParams.videoFormat) {
        queryParams = queryParams.set('videoFormat', <any>vodQueryParams.videoFormat);
      }
      if (vodQueryParams.aspectRatio) {
        queryParams = queryParams.set('aspectRatio', <any>vodQueryParams.aspectRatio);
      }
      if (vodQueryParams.imdbRating) {
        queryParams = queryParams.set('imdbRating', <any>vodQueryParams.imdbRating);
      }
      if (vodQueryParams.provider && vodQueryParams.provider.length > 0) {
        queryParams = queryParams.set('provider', <any>vodQueryParams.provider);
      }
      if (vodQueryParams.quickFilter) {
        queryParams = queryParams.set('quickFilter', <any>vodQueryParams.quickFilter);
      }
      if (vodQueryParams.licenseTba) {
        queryParams = queryParams.set('licenseTba', <any>vodQueryParams.licenseTba);
      }
      if (vodQueryParams.videoOperationFailed) {
        queryParams = queryParams.set('videoOperationFailed', <any>vodQueryParams.videoOperationFailed);
      }
      if (vodQueryParams.videoOperationProcessStatus) {
        queryParams = queryParams.set('videoOperationProcessStatus', <any>vodQueryParams.videoOperationProcessStatus);
      }
      if (vodQueryParams.migrated != undefined) {
        queryParams = queryParams.set('migrated', <any>vodQueryParams.migrated);
      }
      if (vodQueryParams.labelId.length) {
        queryParams = queryParams.set('labelId', <any>vodQueryParams.labelId);
      }
      if (vodQueryParams.contentGroupIds.length) {
        queryParams = queryParams.set('contentGroupId', <any>vodQueryParams.contentGroupIds);
      }
      if (vodQueryParams.blackPeriods != undefined) {
        queryParams = queryParams.set('licenseBlackPeriod', <any>vodQueryParams.blackPeriods);
      }
      if (vodQueryParams.mwInternalCode) {
        queryParams = queryParams.set('mwInternalCode', <any>vodQueryParams.mwInternalCode);
      }
      if (vodQueryParams.videoOperationNotAssigned != undefined) {
        queryParams = queryParams.set('videoOperationNotAssigned', <any>vodQueryParams.videoOperationNotAssigned);
      }
      if (vodQueryParams.isValidPoster != undefined) {
        queryParams = queryParams.set('isValidPoster', <any>vodQueryParams.isValidPoster);
      }
      if (vodQueryParams.isValidMetadata != undefined) {
        queryParams = queryParams.set('isValidMetadata', <any>vodQueryParams.isValidMetadata);
      }
      if (vodQueryParams.planStartTime) {
        queryParams = queryParams.set('planStartTime', <any>vodQueryParams.planStartTime);
      }
      if (vodQueryParams.planEndTime) {
        queryParams = queryParams.set('planEndTime', <any>vodQueryParams.planEndTime);
      }
      if (vodQueryParams.genreIds.length) {
        queryParams = queryParams.set('genreId', <any>vodQueryParams.genreIds);
      }
      if (vodQueryParams.withoutMetadata != undefined) {
        queryParams = queryParams.set('withoutMetadata', <any>vodQueryParams.withoutMetadata);
      }
      if (vodQueryParams.withoutPlan != undefined) {
        queryParams = queryParams.set('withoutPlan', <any>vodQueryParams.withoutPlan);
      }
      if (vodQueryParams.withoutPoster != undefined) {
        queryParams = queryParams.set('withoutPoster', <any>vodQueryParams.withoutPoster);
      }
      if (vodQueryParams.vfoContentReport != undefined) {
        queryParams = queryParams.set('vfoContentReport', <any>vodQueryParams.vfoContentReport);
      }
      if (vodQueryParams.publishTime) {
        queryParams = queryParams.set('publishTime', <any>vodQueryParams.publishTime);
      }
      if (vodQueryParams.unpublishedTime) {
        queryParams = queryParams.set('unpublishedTime', <any>vodQueryParams.unpublishedTime);
      }
      if (vodQueryParams.castId) {
        queryParams = queryParams.set('castId', <any>vodQueryParams.castId);
      }
      if (vodQueryParams.metadataSummary) {
        queryParams = queryParams.set('metadataSummary', <any>vodQueryParams.metadataSummary.trim());
      }
      if (vodQueryParams.trailerWaiting != undefined) {
        queryParams = queryParams.set('trailerWaiting', <any>vodQueryParams.trailerWaiting);
      }
      if (vodQueryParams.isHaveTrailer != undefined) {
        queryParams = queryParams.set('isHaveTrailer', <any>vodQueryParams.isHaveTrailer);
      }
      if (vodQueryParams.trailerAssignee) {
        queryParams = queryParams.set('trailerAssignee', <any>vodQueryParams.trailerAssignee);
      }
      if (vodQueryParams.trailerCompletionTime) {
        queryParams = queryParams.set('trailerCompletionTime', <any>vodQueryParams.trailerCompletionTime);
      }
      if (vodQueryParams.trailerCompleted != undefined) {
        queryParams = queryParams.set('trailerCompleted', <any>vodQueryParams.trailerCompleted);
      }
      if (vodQueryParams.trailerPriority && vodQueryParams.trailerPriority.length > 0) {
        queryParams = queryParams.set('trailerPriority', <any>vodQueryParams.trailerPriority);
      }
      if (vodQueryParams.trailerPromo && vodQueryParams.trailerPromo.length > 0) {
        queryParams = queryParams.set('trailerPromo', <any>vodQueryParams.trailerPromo);
      }
      if (vodQueryParams.trailerOperationProcessNames) {
        queryParams = queryParams.set('trailerOperationProcessNames', <any>vodQueryParams.trailerOperationProcessNames);
      }
      if (vodQueryParams.trailerNotCompleted != undefined) {
        queryParams = queryParams.set('trailerNotCompleted', <any>vodQueryParams.trailerNotCompleted);
      }
      if (vodQueryParams.broadCastTime && vodQueryParams.broadCastTime.length > 0) {
        queryParams = queryParams.set('broadCastTime', <any>vodQueryParams.broadCastTime);
      }
      if (vodQueryParams.country) {
        queryParams = queryParams.set('country', <any>vodQueryParams.country);
      }
      if (vodQueryParams.originalLanguage) {
        queryParams = queryParams.set('originalLanguage', <any>vodQueryParams.originalLanguage);
      }
    }
    return this.http.get<any>(this.contentsApiUrl + '/report', { params: queryParams, responseType: 'text' as 'json' });
  }

  rescueContent(contentId: number): Observable<number> {
    return this.http.get<number>(this.contentsApiUrl + `/${contentId}/undo`, { responseType: 'text' as 'json' });
  }

  getComingSoonContentById(contentId: number): Observable<ComingSoonContent> {
    return this.http.get<ComingSoonContent>(this.contentsApiUrl + `/${contentId}/comingsoon`);
  }

  trailerOperationProcess(contentId: number, operation: string, action: string, processRequest?: ProcessRequest): Observable<string> {
    return this.http.post<string>(this.contentsApiUrl + `/${contentId}/traileroperationprocesses/${operation}/${action}`, processRequest, {
      responseType: 'text' as 'json',
    });
  }

  directTrailerProcess(contentId: number, trailerOperationName: string, processRequest: ProcessRequest): Observable<string> {
    return this.http.post<string>(this.contentsApiUrl + `/${contentId}/traileroperationprocesses/${trailerOperationName}/direct`, processRequest, {
      responseType: 'text' as 'json',
    });
  }

  reprocessTrailerOperation(contentId: number, trailerOperationName: string): Observable<string> {
    return this.http.post<string>(this.contentsApiUrl + `/${contentId}/traileroperationprocesses/${trailerOperationName}/reprocess`, null, {
      responseType: 'text' as 'json',
    });
  }

  getNotApprovedContents(queryParam: QueryParams): Observable<PageOfNotApprovedContentResponse> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (queryParam.page > 0) {
      queryParams = queryParams.set('page', <any>queryParam.page);
    }
    if (queryParam.itemCount) {
      queryParams = queryParams.set('itemCount', <any>queryParam.itemCount);
    }
    if (queryParam.sortParam) {
      queryParams = queryParams.set('sortParam', <any>queryParam.sortParam);
    }
    if (queryParam.issueTypes) {
      queryParams = queryParams.set('issueTypes', <any>queryParam.issueTypes);
    }

    return this.http.get<PageOfNotApprovedContentResponse>(this.contentsApiUrl + '/notapproved', { params: queryParams });
  }

  checkApprovedStatus(contentId: number): Observable<string> {
    if (this.checkApprovedStatusCache[contentId]) {
      return this.checkApprovedStatusCache[contentId];
    }

    this.checkApprovedStatusCache[contentId] = this.http.get<string>(`${this.contentsApiUrl}/${contentId}/checkapprovedstatus`);

    return this.checkApprovedStatusCache[contentId];
  }

  clearCheckApprovedStatusCache(contentId: number) {
    delete this.checkApprovedStatusCache[contentId];
  }

  videoOperationBulkProcess(contentIds: number[], processName: string, action: string, processRequest: ProcessRequest): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (contentIds) {
      queryParams = queryParams.set('contentId', <any>contentIds);
    }
    // /contents/bulk/videooperationprocesses/{videoOperationName}/direct
    return this.http.post<string>(this.contentsApiUrl + `/bulk/videooperationprocesses/${processName}/${action}`, processRequest, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  videoOperationBulkDirect(contentIds: number[], processName: string, processRequest: ProcessRequest): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (contentIds) {
      queryParams = queryParams.set('contentId', <any>contentIds);
    }
    // /contents/bulk/videooperationprocesses/{videoOperationName}/direct
    return this.http.post<string>(this.contentsApiUrl + `/bulk/videooperationprocesses/${processName}/direct`, processRequest, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  getThirdPartyReport(contentIds: number[]): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (contentIds) {
      queryParams = queryParams.set('contentIds', <any>contentIds);
    }
    return this.http.get<string>(this.contentsApiUrl + '/report/thirdparty', {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  // /traileroperationtasks/trailerfinishedcontentscount
  getTrailerFinishedContentsCount(searchTime: number): Observable<TotalFinishedTaskCountByUserResponse[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (searchTime != null) {
      queryParams = queryParams.set('finishedTime', <any>searchTime);
    }
    return this.http.get<TotalFinishedTaskCountByUserResponse[]>(this.contentsApiUrl + `/trailerfinishedcontentscountbyuser`, {
      params: queryParams,
    });
  }

  getVfoFinishedContentsCount(finishedTime: number): Observable<VideoOperationUserTaskCountResponse[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (finishedTime != null) {
      queryParams = queryParams.set('finishedTime', <any>finishedTime);
    }
    return this.http.get<TotalFinishedTaskCountByUserResponse[]>(this.contentsApiUrl + `/videofileprocessfinishedcontentscountbyuser`, {
      params: queryParams,
    });
  }

  trailerOperationBulkDirect(contentIds: number[], trailerOperationName: string, processRequest: ProcessRequest): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (contentIds) {
      queryParams = queryParams.set('contentId', <any>contentIds);
    }
    return this.http.post<string>(this.contentsApiUrl + `/bulk/traileroperationprocesses/${trailerOperationName}/direct`, processRequest, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  trailerOperationBulkProcess(
    contentIds: number[],
    trailerOperationName: string,
    action: string,
    processRequest: ProcessRequest,
  ): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (contentIds) {
      queryParams = queryParams.set('contentId', <any>contentIds);
    }

    return this.http.post<string>(this.contentsApiUrl + `/bulk/traileroperationprocesses/${trailerOperationName}/${action}`, processRequest, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  setComingSoon(contentId: number, enable: boolean): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (enable != undefined && enable != null) {
      queryParams = queryParams.set('enable', <any>enable);
    }
    return this.http.put<string>(this.contentsApiUrl + `/${contentId}/comingsoon`, null, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  setEncryption(contentId: number, enable: boolean): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (enable != undefined && enable != null) {
      queryParams = queryParams.set('enable', <any>enable);
    }
    return this.http.put<string>(this.contentsApiUrl + `/${contentId}/encryption`, null, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  setNotInChildProfile(contentId: number, enable: boolean): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (enable != undefined && enable != null) {
      queryParams = queryParams.set('enable', <any>enable);
    }
    return this.http.put<string>(this.contentsApiUrl + `/${contentId}/notinchildprofile`, null, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  getTrailerOperationProcessesMonthlyCompletedCounts(): Observable<TrailerOperationProcessesCompletedCount[]> {
    return this.http.get<TrailerOperationProcessesCompletedCount[]>(this.contentsApiUrl + `/completedtraileroperationprocessesmonthly/count`);
  }

  getTrailerOperationProcessesYearlyCompletedCounts(): Observable<TrailerOperationProcessesCompletedCount[]> {
    return this.http.get<TrailerOperationProcessesCompletedCount[]>(this.contentsApiUrl + `/completedtraileroperationprocessesyearly/count`);
  }

  // /completedtraileroperationscountbyuser/count

  getCompletedTrailersCountByUser(completionTime: number[], completionMonth: number): Observable<TotalFinishedTaskCountByUserResponse[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    let time: number[];
    let month = 0;
    if (!completionTime) {
      time = [moment().startOf('y').toDate().getTime(), moment().endOf('y').toDate().getTime()];
    } else {
      time = completionTime;
    }
    if (!completionMonth) {
      month = 0;
    } else {
      month = completionMonth;
    }

    queryParams = queryParams.set('completionTime', <any>time);
    queryParams = queryParams.set('completionMonth', <any>month);
    return this.http.get<TotalFinishedTaskCountByUserResponse[]>(this.contentsApiUrl + `/completedtraileroperationscountbyuser/count`, {
      params: queryParams,
    });
  }

  getFinishedTrailerTaskByPromo(yaerlyFinishedTaskByPromo: number): Observable<TotalCompletedPromoCountByUserAndPromoTypeResponse[]> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (yaerlyFinishedTaskByPromo != null) {
      queryParams = queryParams.set('year', <any>moment({ year: yaerlyFinishedTaskByPromo }).startOf('y').toDate().getTime());
    }
    return this.http.get<TotalCompletedPromoCountByUserAndPromoTypeResponse[]>(this.contentsApiUrl + `/completedpromoyearly/count`, {
      params: queryParams,
    });
  }

  validationRecheck(contentId: number): Observable<any> {
    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/validation/recheck`, null, { responseType: 'text' as 'json' });
  }

  validationCheck(contentId: number, platformComments: ValidationEventsPlatformComment[]): Observable<any> {
    return this.http.post<any>(this.contentsApiUrl + `/${contentId}/validation/check`, platformComments, { responseType: 'text' as 'json' });
  }

  getContentsWithBannerPoster(vodQueryParams?: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (vodQueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodQueryParams.itemCount);
    }
    if (vodQueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
    }
    if (vodQueryParams.status && vodQueryParams.status.length > 0) {
      queryParams = queryParams.set('status', <any>vodQueryParams.status);
    }
    if (vodQueryParams.type && vodQueryParams.type.length > 0) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.sortParam && vodQueryParams.sortParam.length) {
      queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
    }

    return this.http.get<any>(this.contentsApiUrl + '/withoutBanner', {
      params: queryParams,
      observe: 'response',
    });
  }

  getContentsValidBanner(vodQueryParams?: VodQueryParams): Observable<any> {
    let queryParams = new HttpParams({
      encoder: new CustomHttpUrlEncodingCodec(),
    });

    if (vodQueryParams.page > 0) {
      queryParams = queryParams.set('page', <any>vodQueryParams.page);
    }
    if (vodQueryParams.itemCount) {
      queryParams = queryParams.set('itemCount', <any>vodQueryParams.itemCount);
    }
    if (vodQueryParams.contentName) {
      queryParams = queryParams.set('contentName', <any>vodQueryParams.contentName);
    }
    if (vodQueryParams.status && vodQueryParams.status.length > 0) {
      queryParams = queryParams.set('status', <any>vodQueryParams.status);
    }
    if (vodQueryParams.type && vodQueryParams.type.length > 0) {
      queryParams = queryParams.set('type', <any>vodQueryParams.type);
    }
    if (vodQueryParams.sortParam && vodQueryParams.sortParam.length) {
      queryParams = queryParams.set('sortParam', <any>vodQueryParams.sortParam);
    }

    return this.http.get<any>(this.contentsApiUrl + '/validBanner', {
      params: queryParams,
      observe: 'response',
    });
  }

  setSectionInfo(contentId: number, enable: boolean): Observable<string> {
    let queryParams = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });
    if (enable != undefined && enable != null) {
      queryParams = queryParams.set('enable', <any>enable);
    }
    return this.http.put<string>(this.contentsApiUrl + `/${contentId}/sectioninfo`, null, {
      params: queryParams,
      responseType: 'text' as 'json',
    });
  }

  copyContent(contentId: number): Observable<string> {

    return this.http.post<string>(this.contentsApiUrl + `/${contentId}/copy`, null, {
      responseType: 'text' as 'json',
    });
  }

  createVodBanner(
    contentId: number,
    menuItemId: number,
    platform: BannerPlatforms,
    status: boolean,
    sequence: number
  ): Observable<number> {

    const url = `${this.bannerApiUrl}/${contentId}/add/${menuItemId}`;

    const params = new HttpParams()
      .set('platform', String(platform))
      .set('status', String(status))
      .set('sequence', String(sequence));

    return this.http.post<number>(url, null, { params });
  }

  getVodBannerById(id: number): Observable<NewBanner> {
    const url = `${this.bannerApiUrl}/${id}`;
    return this.http.get<NewBanner>(url);
  }

  getAllVodBanners(): Observable<NewBanner[]> {
    return this.http.get<NewBanner[]>(this.bannerApiUrl);
  }

  getVodBannersOfMenuItem(menuItemId: number): Observable<NewBanner[]> {
    const url = `${this.bannerApiUrl}/contents/${menuItemId}`;
    return this.http.get<NewBanner[]>(url);
  }

  updateVodBanner(banner: NewBanner): Observable<NewBanner> {
    return this.http.put<NewBanner>(this.bannerApiUrl, banner);
  }

  deleteVodBanner(id: number): Observable<string> {
    const url = `${this.bannerApiUrl}/${id}`;
    return this.http.delete(url, { responseType: 'text' });
  }

  filterVodBanners(options: {
    pageable?: boolean;
    page?: number;
    itemCount?: number;
    contentName?: string;
    type?: string;
    platform?: string;
    menuItem?: number;
    status?: boolean;
    vodType?: string;
    sortParam?: string[];
  }): Observable<any> {

    const url = `${this.bannerApiUrl}/filter`;

    let params = new HttpParams();
    Object.entries(options || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') return;

      if (Array.isArray(v)) {
        v.forEach(item => params = params.append(k, String(item)));
      } else {
        params = params.set(k, String(v));
      }
    });

    return this.http.get<any>(url, { params });
  }

  updateVodBannerSequence(banners: any[]): Observable<any> {
    return this.http.put<any>(this.bannerApiUrl + '/sequence', banners);
  }

  updateVodBannerStatus(id: number, status: boolean): Observable<any> {
    var url = this.bannerApiUrl + '/' + id + '/status';
    var params = new HttpParams().set('status', String(status));
    return this.http.put<any>(url, null, { params: params });
  }

  searchParents(text: string, childType: string): Observable<Content[]> {
    return this.http.get<Content[]>(this.contentsApiUrl + `/parentsearch`, {
      params: { text, childType }
    });
  }

  updateContentParent(contentId: number, parentId: number,
    unpublishIfNotPublishable: boolean = false): Observable<string> {
    return this.http.put(this.contentsApiUrl + `/${contentId}/parent/${parentId}`, {}, {
      params: { unpublishIfNotPublishable: String(unpublishIfNotPublishable) },
      responseType: 'text'
    });
  }

  /** Dry-run: tells whether moving the content under this parent forces an unpublish. */
  precheckContentParent(contentId: number, parentId: number): Observable<ParentChangePrecheck> {
    return this.http.get<ParentChangePrecheck>(
      this.contentsApiUrl + `/${contentId}/parent/${parentId}/precheck`);
  }

  addLanguageOptions(contentId: number, request: LanguageOptionsRequest): Observable<any> {
    return this.http.post(this.contentsApiUrl + `/${contentId}/languageoptions/add`, request, {
      responseType: 'text' as 'json',
    });
  }

  updateLanguageOptions(contentId: number, request: LanguageOptionsRequest): Observable<any> {
    return this.http.put(this.contentsApiUrl + `/${contentId}/languageoptions/update`, request, {
      responseType: 'text' as 'json',
    });
  }

  removeLanguageOptions(contentId: number, request: LanguageOptionsRequest): Observable<any> {
    return this.http.request<any>('delete', this.contentsApiUrl + `/${contentId}/languageoptions/remove`, {
      body: request,
      responseType: 'text' as 'json',
    });
  }

  resyncLanguageOptionsFromSmil(contentId: number): Observable<any> {
    return this.http.post(this.contentsApiUrl + `/${contentId}/languageoptions/resync-from-smil`, {}, {
      responseType: 'text' as 'json',
    });
  }

  getContentPlanRange(contentId: number): Observable<number[]> {
    return this.http.get<number[]>(this.contentsApiUrl + `/${contentId}/plantimerange`, {
    });
  }

  isContentAvaliable(contentId: number): Observable<boolean> {
    return this.http.get<boolean>(this.contentsApiUrl + `/avaliable/${contentId}`, {
    });
  }

  getChildCount(contentId: number): Observable<number> {
    return this.http.get<number>(this.contentsApiUrl + `/${contentId}/childs/count`);
  }
}
